import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Public,
  CheckCircle,
  FlightTakeoff,
  LocalShipping,
  Description,
  Security,
  Language,
  Euro
} from '@mui/icons-material';

function InternationalMove() {
  const countries = [
    'Schweiz', 'Österreich', 'Frankreich', 'Niederlande', 
    'Belgien', 'Polen', 'Tschechien', 'Italien', 
    'Spanien', 'Großbritannien', 'Dänemark', 'Schweden'
  ];

  const services = [
    {
      icon: <Description sx={{ color: '#55d6dc' }} />,
      title: 'Zollabwicklung',
      description: 'Komplette Abwicklung aller Zollformalitäten und Dokumentation'
    },
    {
      icon: <LocalShipping sx={{ color: '#55d6dc' }} />,
      title: 'Internationale Logistik',
      description: 'Koordination von Land-, See- und Lufttransport'
    },
    {
      icon: <Security sx={{ color: '#55d6dc' }} />,
      title: 'Transportversicherung',
      description: 'Umfassender Versicherungsschutz für internationale Transporte'
    },
    {
      icon: <Language sx={{ color: '#55d6dc' }} />,
      title: 'Mehrsprachiger Service',
      description: 'Betreuung in Deutsch, Englisch und weiteren Sprachen'
    }
  ];

  const processSteps = [
    { title: 'Beratung', desc: 'Analyse Ihrer Anforderungen und Zielland-Bestimmungen' },
    { title: 'Planung', desc: 'Detaillierte Umzugsplanung und Kostenvoranschlag' },
    { title: 'Dokumentation', desc: 'Vorbereitung aller erforderlichen Dokumente' },
    { title: 'Verpackung', desc: 'Export-gerechte Verpackung Ihrer Güter' },
    { title: 'Transport', desc: 'Durchführung des internationalen Transports' },
    { title: 'Zollabwicklung', desc: 'Abwicklung aller Zollformalitäten' },
    { title: 'Zustellung', desc: 'Lieferung und Aufbau am Zielort' }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Public sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Auslandsumzug
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Ihr Partner für internationale Umzüge
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Ob innerhalb Europas oder weltweit – wir organisieren Ihren Auslandsumzug 
            professionell und kümmern uns um alle Details von der Zollabwicklung bis zur Zustellung.
          </Typography>
        </Box>

        {/* Countries Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
            Unsere häufigsten Zielländer
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {countries.map((country) => (
              <Paper
                key={country}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: '#1a3a44',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#55d6dc',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <FlightTakeoff sx={{ fontSize: 20, color: '#55d6dc' }} />
                  <Typography>{country}</Typography>
                </Stack>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card sx={{
                bgcolor: '#1a3a44',
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                height: '100%'
              }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ 
                      bgcolor: 'rgba(85,214,220,0.12)', 
                      borderRadius: '50%', 
                      p: 1.5,
                      display: 'flex'
                    }}>
                      {service.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', opacity: 0.85 }}>
                        {service.description}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Process Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Ablauf eines Auslandsumzugs
          </Typography>
          <Card sx={{ bgcolor: '#1a3a44', p: 3 }}>
            <Stepper orientation="vertical">
              {processSteps.map((step, index) => (
                <Step key={index} active={true}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        color: '#55d6dc !important',
                        '& .MuiStepIcon-text': { fill: '#0a1f28' }
                      }
                    }}
                  >
                    <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                      {step.title}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography sx={{ color: 'white', opacity: 0.85 }}>
                      {step.desc}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Card>
        </Box>

        {/* Important Info */}
        <Card sx={{ 
          bgcolor: 'rgba(255,215,0,0.1)', 
          border: '2px solid #ffd700',
          mb: 6
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ color: 'white', mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Euro sx={{ color: '#ffd700' }} />
              Wichtige Informationen
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle sx={{ color: '#ffd700' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Frühzeitige Planung empfohlen (mindestens 8-12 Wochen vorher)"
                  sx={{ color: 'white' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle sx={{ color: '#ffd700' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Zollbestimmungen variieren je nach Zielland"
                  sx={{ color: 'white' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle sx={{ color: '#ffd700' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Transportversicherung dringend empfohlen"
                  sx={{ color: 'white' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle sx={{ color: '#ffd700' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Vollständige Inventarliste erforderlich"
                  sx={{ color: 'white' }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: '#275664', 
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Planen Sie Ihren Auslandsumzug mit uns
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Kostenlose Beratung und detailliertes Angebot für Ihren internationalen Umzug
          </Typography>
          <Box
            component="button"
            onClick={() => window.location.href = '/contact'}
            style={{
              backgroundColor: '#55d6dc',
              color: '#275664',
              padding: '12px 32px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
          >
            Beratungstermin vereinbaren
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default InternationalMove;